import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { ArrowRight, Loader2 } from "lucide-react";
import api, { visitorLang } from "../lib/api";
import PhoneInput, { defaultDial, fullPhone, phoneValid } from "./PhoneInput";

import { tr } from "../i18n";
const clientTypes = [
  "Business",
  "Personal",
  "Charity / School",
  "Accountant",
  "Adviser",
];

export default function AccountModal({ open, mode, onClose, onSuccess }) {
  const isIllustration = mode === "illustration";
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    dial: defaultDial(),
    clientType: "Business",
    amount: "",
  });

  useEffect(() => {
    if (open) {
      setError("");
      setForm({ name: "", email: "", phone: "", dial: defaultDial(), clientType: "Business", amount: "" });
    }
  }, [open, mode]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!phoneValid(form.phone)) {
      setError(tr("Please enter a valid phone number."));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const firstName = form.name.split(" ")[0] || "there";
      const { dial, ...rest } = form;
      await api.post("/leads", { ...rest, phone: fullPhone(dial, form.phone), mode, lang: visitorLang() });
      // Close cleanly first, then show a separate confirmation dialog.
      onClose();
      if (onSuccess) onSuccess(firstName);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          tr("We couldn't submit your request. Please try again.")
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[480px] p-0 overflow-hidden gap-0 border-0">
        {/* Header band */}
        <div className="bg-[#0b1f3a] px-7 pt-7 pb-6">
          <DialogHeader>
            <p className="text-[#c2a15a] text-[11px] tracking-[0.25em] uppercase mb-2">
              {tr("R&H Savings")}
            </p>
            <DialogTitle className="font-display text-white text-[26px] leading-tight">
              {isIllustration ? tr("Request an illustration") : tr("Open an account")}
            </DialogTitle>
            <DialogDescription className="text-white/60 text-[14px] mt-1">
              {isIllustration
                ? tr("Tell us about your deposit and we'll prepare a tailored illustration.")
                : tr("Start a single application to access 55+ banks and building societies.")}
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={handleSubmit} className="px-7 py-6 space-y-4">
          <Field label={tr("Full name")}>
            <input
              required
              value={form.name}
              onChange={update("name")}
              placeholder={tr("Jane Smith")}
              className="rh-input"
            />
          </Field>
          <div className="grid gap-4">
            <Field label={tr("Email")}>
              <input
                required
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder={tr("jane@company.com")}
                className="rh-input"
              />
            </Field>
            <Field label={tr("Phone")}>
              <PhoneInput testid="illustration-phone-input" dial={form.dial} number={form.phone}
                onDial={(v) => setForm({ ...form, dial: v })} onNumber={(v) => setForm({ ...form, phone: v })}
                placeholder="20 7946 0321" />
            </Field>
          </div>
          <Field label={tr("Client type")}>
            <select
              value={form.clientType}
              onChange={update("clientType")}
              className="rh-input bg-white"
            >
              {clientTypes.map((c) => (
                <option key={c} value={c}>{tr(c)}</option>
              ))}
            </select>
          </Field>
          <Field label={tr("Approximate deposit amount")}>
            <input
              value={form.amount}
              onChange={update("amount")}
              placeholder="£1,000,000"
              className="rh-input"
            />
          </Field>

          {error && (
            <p className="text-[13px] text-red-600 bg-red-50 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            className="group w-full inline-flex items-center justify-center gap-2 bg-[#c2a15a] hover:bg-[#d8bd82] disabled:opacity-60 text-[#0b1f3a] font-semibold uppercase tracking-wide text-[14px] px-6 py-4 rounded-full transition-colors mt-2"
          >
            {busy ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <>
                {isIllustration ? tr("Request illustration") : tr("Start application")}
                <ArrowRight
                  size={17}
                  className="transition-transform group-hover:translate-x-1"
                />
              </>
            )}
          </button>
          <p className="text-[11px] text-[#0b1f3a]/45 text-center leading-relaxed">
            {tr("By submitting, you agree to be contacted by R&H. This is a demo form — no sensitive data is required.")}
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[13px] font-medium text-[#0b1f3a]/70 mb-1.5">
      {label}
    </span>
    {children}
  </label>
);
